/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { faEnvelope, faLock } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { signIn } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { managerV1BaseUrl } from '../../../utils';
import { useAuthErrorMessage } from '../../../hooks';
import { AuthenticationException, MFAMethod } from '../../../services';
import { z } from 'zod';
import { Alert, AlertDescription } from '@e2n/ui';
import { Link } from '@/components';
import * as Sentry from '@sentry/react';
import { useAppForm } from '@/components/form';

function ErrorMessage({
  serverError,
}: {
  serverError?: Partial<{
    type: string | number;
    message: string;
  }>;
}) {
  const { t } = useTranslation();
  const message = useAuthErrorMessage(
    serverError?.type as AuthenticationException,
  );
  return (
    <Alert data-testid="login-error-callout" variant="destructive">
      <AlertDescription>
        {message}{' '}
        <Link
          className="hover:text-primary-red-600"
          href="https://hilfe.e2n.de"
          target="_blank">
          {t('pages.login.loginForm.linkTitle')}
        </Link>
        {'.'}
      </AlertDescription>
    </Alert>
  );
}

export function LoginForm({
  setMfaChallengeType,
}: {
  setMfaChallengeType: Dispatch<SetStateAction<MFAMethod>>;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        username: z
          .string()
          .trim()
          .min(1, { message: t('pages.login.loginForm.emailIsRequired') }),
        password: z
          .string()
          .min(1, { message: t('pages.login.loginForm.passwordIsRequired') }),
      }),
    [t],
  );

  const form = useAppForm({
    validators: {
      onSubmit: schema,
    },
    defaultValues: { username: '', password: '' },
    onSubmit: async ({ value, formApi }) => {
      try {
        const signInOutput = await signIn({
          username: value.username,
          password: value.password,
        });
        if (signInOutput.nextStep.signInStep === 'DONE') {
          navigate({ pathname: '/' }, { replace: true });
        } else if (
          signInOutput.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
        ) {
          setMfaChallengeType('TOTP');
        }
      } catch (err) {
        Sentry.captureException(err);
        formApi.setErrorMap({
          onSubmit: {
            form: {
              serverError: {
                type: err?.name ?? 'UnknownError',
                message:
                  err?.message ??
                  t('pages.login.loginForm.unknownError', {
                    error: err?.message ?? err?.name ?? '',
                  }),
              },
            },
            fields: {},
          },
        });
      }
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      autoComplete="off">
      <form.Subscribe selector={(state) => state.errors}>
        {(errors) => {
          const errorArray = Array.isArray(errors) ? errors : [errors];
          const serverError = errorArray.find(
            (err) => err && 'serverError' in err && err.serverError,
          )?.serverError;

          const formattedError = Array.isArray(serverError)
            ? serverError[0]
            : serverError;

          return formattedError ? (
            <ErrorMessage serverError={formattedError} />
          ) : null;
        }}
      </form.Subscribe>

      <form.AppField name="username">
        {(field) => (
          <field.TextInput
            startIcon={faEnvelope}
            placeholder={t('pages.login.loginForm.eMailPlaceholder')}
            data-testid="login-username-input-field"
            label={t('pages.login.loginForm.eMailLabel')}
            isRequired
          />
        )}
      </form.AppField>
      <form.AppField name="password">
        {(field) => (
          <field.TextInput
            startIcon={faLock}
            data-testid="login-password-input-field"
            label={t('pages.login.loginForm.passwordLabel')}
            isRequired
            type="password"
            autoComplete="password"
            placeholder={t('pages.login.loginForm.passwordPlaceholder')}
          />
        )}
      </form.AppField>
      <div className="flex justify-end">
        <Link
          className="text-sm"
          data-testid="login-forgot-password-link"
          href={`${managerV1BaseUrl}/forgot.jsf`}>
          {t('pages.login.forgotPassword.linkTitle')}
        </Link>
      </div>
      <form.AppForm>
        <form.SubmitButton
          buttonText={t('pages.login.loginForm.buttonLabel')}
          data-testid="login-submit-button"
          size="sm"></form.SubmitButton>
      </form.AppForm>
    </form>
  );
}
